/** Small framework-independent math used by the GPU command frontend. */
export const TAU = Math.PI * 2;
export const clamp = (x, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));
export const identity = () => ({ a: 1, b: 0, c: 0, d: 1, x: 0, y: 0 });
export function multiply(m, r) {
  return { a: m.a*r.a+m.c*r.b, b: m.b*r.a+m.d*r.b,
    c: m.a*r.c+m.c*r.d, d: m.b*r.c+m.d*r.d,
    x: m.a*r.x+m.c*r.y+m.x, y: m.b*r.x+m.d*r.y+m.y };
}
export const apply = (m, x, y) => [m.a*x+m.c*y+m.x, m.b*x+m.d*y+m.y];
export function normalizePoint(point) {
  const array = Array.isArray(point) || ArrayBuffer.isView(point);
  const x = array ? point[0] : point?.x, y = array ? point[1] : point?.y;
  const pressure = (array ? point[2] : point?.pressure) ?? 1;
  if (!Number.isFinite(x) || !Number.isFinite(y)) throw new TypeError('Point coordinates must be finite numbers.');
  if (!Number.isFinite(pressure) || pressure < 0) throw new RangeError('Point pressure must be finite and nonnegative.');
  return [x, y, pressure];
}
export function inverse(m) {
  const det = m.a*m.d-m.b*m.c;
  if (!Number.isFinite(det) || Math.abs(det) < 1e-14) return null;
  return [m.d/det,-m.b/det,-m.c/det,m.a/det,
    (m.c*m.y-m.d*m.x)/det,(m.b*m.x-m.a*m.y)/det];
}
export function hash(value) {
  const str = String(value); let h = 2166136261;
  for (let i=0;i<str.length;i++) h = Math.imul(h ^ str.charCodeAt(i),16777619);
  return h >>> 0;
}
export class Random {
  constructor(seed = 1) { this.seed(seed); }
  seed(value) { this.state = hash(value); return this; }
  next() {
    this.state = (this.state + 0x6d2b79f5) | 0;
    let t = Math.imul(this.state ^ this.state>>>15, this.state|1);
    t ^= t + Math.imul(t ^ t>>>7, t|61);
    return ((t ^ t>>>14)>>>0) / 4294967296;
  }
  range(a=0,b=1) { return a+(b-a)*this.next(); }
}
export function noise2(x,y,seed=0) {
  const random = (a,b) => {
    let t = Math.imul(a|0,374761393) + Math.imul(b|0,668265263) + (seed|0);
    t = Math.imul(t ^ t>>>13,1274126177);
    return ((t ^ t>>>16)>>>0)/4294967296;
  };
  const ix=Math.floor(x),iy=Math.floor(y),fx=x-ix,fy=y-iy;
  const u=fx*fx*(3-2*fx),v=fy*fy*(3-2*fy);
  const a=random(ix,iy),b=random(ix+1,iy),c=random(ix,iy+1),d=random(ix+1,iy+1);
  return a+(b-a)*u+(c-a)*v+(a-b-c+d)*u*v;
}
export function bounds(paths, transform=identity(), padding=0) {
  let x0=Infinity,y0=Infinity,x1=-Infinity,y1=-Infinity;
  for (const path of paths) for (const p of path) {
    const q=apply(transform,p[0],p[1]);
    x0=Math.min(x0,q[0]);y0=Math.min(y0,q[1]);x1=Math.max(x1,q[0]);y1=Math.max(y1,q[1]);
  }
  return [x0-padding,y0-padding,x1+padding,y1+padding];
}
export function segments(points, closed=false) {
  if (!Array.isArray(points)) throw new TypeError('A path requires an array of points.');
  points = points.map(normalizePoint);
  const n=points.length-(closed?0:1), values=[], pressures=[];
  let totalLength=0;
  for (let i=0;i<n;i++) {
    const a=points[i],b=points[(i+1)%points.length];
    const length=Math.hypot(b[0]-a[0],b[1]-a[1]);
    if (length < 1e-8) continue;
    values.push(a[0],a[1],b[0],b[1],totalLength,length);
    pressures.push(a[2]??1,b[2]??1); totalLength+=length;
  }
  const packed = new Float32Array(values), packedPressures = new Float32Array(pressures);
  if (!Number.isFinite(Math.fround(totalLength)) || packed.some(value=>!Number.isFinite(value)) || packedPressures.some(value=>!Number.isFinite(value))) throw new RangeError('Path values exceed the GPU numeric range.');
  return { segments:packed, pressures:packedPressures, totalLength };
}
export function splinePoints(vertices, curvature=0.5, closed=false) {
  if (!Array.isArray(vertices) || vertices.length<2) throw new Error('A spline requires at least two vertices.');
  if (!Number.isFinite(curvature)) throw new TypeError('Spline curvature must be finite.');
  vertices = vertices.map(normalizePoint);
  if (curvature===0 || vertices.length===2) return vertices.map(p=>p.slice());
  const out=[]; const n=vertices.length, steps=n-(closed?0:1);
  const point=i=>vertices[closed?(i+n)%n:Math.max(0,Math.min(n-1,i))];
  for(let i=0;i<steps;i++) {
    const p0=point(i-1),p1=point(i),p2=point(i+1),p3=point(i+2);
    const count=Math.max(2,Math.min(64,Math.ceil(Math.hypot(p2[0]-p1[0],p2[1]-p1[1])/5)));
    for(let j=0;j<count;j++) {
      const t=j/count,t2=t*t,t3=t2*t, h00=2*t3-3*t2+1,h10=t3-2*t2+t,h01=-2*t3+3*t2,h11=t3-t2;
      out.push([h00*p1[0]+h10*(p2[0]-p0[0])*curvature+h01*p2[0]+h11*(p3[0]-p1[0])*curvature,
        h00*p1[1]+h10*(p2[1]-p0[1])*curvature+h01*p2[1]+h11*(p3[1]-p1[1])*curvature,
        (p1[2]??1)*(1-t)+(p2[2]??1)*t]);
    }
  }
  if(!closed) out.push(vertices[n-1].slice());
  return out;
}
/** Scan only contour edges. Particle generation happens later on the GPU. */
export function hatchSegments(paths, spacing, angle, gradient=1) {
  if(!Number.isFinite(spacing)||spacing<=0) throw new RangeError('Hatch spacing must be finite and positive.');
  if(!Number.isFinite(angle)) throw new TypeError('Hatch angle must be finite.');
  if(!Number.isFinite(gradient)||gradient<=0) throw new RangeError('Hatch gradient must be finite and positive.');
  if(!Array.isArray(paths)||paths.some(path=>!Array.isArray(path))) throw new TypeError('Hatching requires arrays of contour points.');
  const c=Math.cos(angle),s=Math.sin(angle);
  const contours=paths.map(p=>p.map(normalizePoint).map(([x,y])=>[x*c-y*s,x*s+y*c]));
  let lo=Infinity,hi=-Infinity;
  for(const p of contours) for(const v of p) {lo=Math.min(lo,v[1]);hi=Math.max(hi,v[1]);}
  const out=[];
  let step=spacing;
  for(let y=lo+step/2, row=0;y<hi;y+=step,row++) {
    if(row>=100000) throw new RangeError('Hatching supports at most 100000 scanlines. Increase hatch spacing.');
    const xs=[];
    for(const p of contours) for(let i=0;i<p.length;i++) {
      const a=p[i],b=p[(i+1)%p.length];
      if((a[1]<=y)!==(b[1]<=y)) xs.push(a[0]+(y-a[1])/(b[1]-a[1])*(b[0]-a[0]));
    }
    xs.sort((a,b)=>a-b);
    for(let i=0;i+1<xs.length;i+=2) out.push([[xs[i]*c+y*s,-xs[i]*s+y*c],[xs[i+1]*c+y*s,-xs[i+1]*s+y*c]]);
    step=Math.max(0.05,step*gradient);
  }
  return out;
}
